import React, { useEffect, useRef } from 'react'
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  DirectionalLight,
  AmbientLight,
  Box3,
  Vector3,
  Color,
  Object3D,
  Mesh,
  Texture,
  Material,
  HemisphereLight,
} from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

export type Model3d = {
  src: string
  background?: string
  height?: string
  autoRotate?: boolean
}

export const MlModel3d: React.FC<Model3d> = ({ src, background = '#f0f0f0', height = '100%', autoRotate = false }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const width = container.clientWidth
    const heightPx = container.clientHeight

    const scene = new Scene()
    scene.background = new Color(background)

    const camera = new PerspectiveCamera(35, width / heightPx, 0.1, 1000)
    camera.position.set(5, 50, 10)

    const renderer = new WebGLRenderer({ antialias: true })
    renderer.setSize(width, heightPx)
    container.appendChild(renderer.domElement)

    const light = new DirectionalLight(0xffffff, 1.2)
    light.position.set(10, 10, 10)
    light.castShadow = true
    scene.add(light)

    const ambientLight = new AmbientLight(0xffffff, 0.6)
    scene.add(ambientLight)

    const hemiLight = new HemisphereLight(0xffffff, 0x444444, 0.4)
    hemiLight.position.set(0, 50, 0)
    scene.add(hemiLight)

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.05

    const loader = new GLTFLoader()
    const controller = new AbortController()
    let currentModel: Object3D | null = null

    loader.load(
      src,
      (gltf) => {
        const model = gltf.scene
        model.traverse((child) => {
          if ((child as any).isMesh) {
            const mesh = child as Mesh
            const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]

            materials.forEach((mat) => {
              Object.entries(mat).forEach(([_, value]) => {
                if (value && (value as any).isTexture) {
                  const tex = value as Texture
                  tex.generateMipmaps = false
                  tex.needsUpdate = true
                }
              })
            })
          }
        })

        const box = new Box3().setFromObject(model)
        const center = box.getCenter(new Vector3())
        model.position.sub(center)

        const sizeVec = box.getSize(new Vector3())
        const maxDim = Math.max(sizeVec.x, sizeVec.y, sizeVec.z)
        const distance = maxDim * 2

        camera.position.set(0, maxDim, distance)
        controls.target.set(0, 0, 0)
        controls.update()

        scene.add(model)
        currentModel = model
        animate()
      },
      undefined,
      (error) => {
        if ((error as any).name === 'AbortError') return
        console.error('Error loading GLTF model:', error)
      }
    )

    const animate = () => {
      requestAnimationFrame(animate)
      if (autoRotate) scene.rotation.y += 0.03
      controls.update()
      renderer.render(scene, camera)
    }

    const resizeObserver = new ResizeObserver(() => {
      const width = container.clientWidth
      const heightPx = container.clientHeight
      renderer.setSize(width, heightPx)
      camera.aspect = width / heightPx
      camera.updateProjectionMatrix()
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      controls.dispose()
      renderer.dispose()
      controller.abort()

      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement)
      }

      if (currentModel) {
        scene.remove(currentModel)
        currentModel.traverse((child) => {
          if ((child as any).geometry) (child as any).geometry.dispose?.()
          if ((child as any).material) {
            const materials = Array.isArray((child as any).material)
              ? (child as any).material
              : [(child as any).material]
            materials.forEach((mat: Material) => mat.dispose?.())
          }
        })
        currentModel = null
      }
    }
  }, [src, background, autoRotate])

  return <div ref={containerRef} style={{ width: '100%', height, position: 'relative' }}></div>
}
